import { createSeedEquipmentRegistry, EQUIPMENT_REGISTRY_SCHEMA_VERSION } from "../data/equipmentRegistrySeed";
import type {
  BikeEquipment,
  EquipmentCategory,
  EquipmentLifecycleStatus,
  EquipmentReadinessStatus,
  EquipmentRegistry,
  EquipmentRegistrySnapshot,
  GymEquipment,
  RecoveryEquipment,
  SensorEquipment
} from "../types/garage";

export const EQUIPMENT_REGISTRY_STORAGE_KEY = "garage.equipment.registry";

export type EquipmentItem = BikeEquipment | RecoveryEquipment | SensorEquipment | GymEquipment;

export type StorageAdapter = {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
};

type DeleteOptions = {
  confirmed: boolean;
};

export type DeleteResult = {
  deleted: boolean;
  requiresConfirmation: boolean;
  warnings: string[];
  registry: EquipmentRegistry;
};

export type EquipmentRegistryRepository = {
  loadRegistry(): EquipmentRegistry;
  saveRegistry(registry: EquipmentRegistry): void;
  addEquipment(item: EquipmentItem): EquipmentRegistry;
  updateEquipment(item: EquipmentItem): EquipmentRegistry;
  archiveEquipment(category: EquipmentCategory, id: string): EquipmentRegistry;
  restoreEquipment(category: EquipmentCategory, id: string): EquipmentRegistry;
  deleteEquipment(category: EquipmentCategory, id: string, options: DeleteOptions): DeleteResult;
  getDeletionWarnings(registry: EquipmentRegistry, category: EquipmentCategory, id: string): string[];
};

class MemoryStorageAdapter implements StorageAdapter {
  private values = new Map<string, string>();

  getItem(key: string): string | null {
    return this.values.get(key) ?? null;
  }

  setItem(key: string, value: string): void {
    this.values.set(key, value);
  }
}

export function createLocalStorageAdapter(): StorageAdapter {
  if (typeof window !== "undefined" && window.localStorage) {
    return window.localStorage;
  }

  return new MemoryStorageAdapter();
}

function nowIso(): string {
  return new Date().toISOString();
}

function cloneRegistry(registry: EquipmentRegistry): EquipmentRegistry {
  return JSON.parse(JSON.stringify(registry)) as EquipmentRegistry;
}

function updateItemTimestamp<T extends { updatedAtIso: string }>(item: T): T {
  return { ...item, updatedAtIso: nowIso() };
}

function migrateV1ToV2(value: unknown): EquipmentRegistry {
  const source = typeof value === "object" && value !== null ? value as Record<string, unknown> : {};
  const bikesValue = Array.isArray(source.bikes) ? source.bikes : [];
  const recoveryValue = Array.isArray(source.recoveryEquipment) ? source.recoveryEquipment : [];
  const sensorsValue = Array.isArray(source.sensors) ? source.sensors : [];

  const migratedBikes: BikeEquipment[] = bikesValue.map((item, index) => {
    const entry = item as Record<string, unknown>;
    const createdAtIso = nowIso();
    const status = entry.status === "maintenance-due" ? "maintenance-due" : "ready";
    const active = entry.active !== false;

    return {
      id: String(entry.id ?? `bike-migrated-${index}`),
      category: "bike",
      lifecycleStatus: active ? "active" : "inactive",
      readinessStatus: active ? status : "unavailable",
      archived: false,
      notes: "",
      createdAtIso,
      updatedAtIso: createdAtIso,
      nickname: String(entry.nickname ?? "Bike"),
      manufacturer: String(entry.manufacturer ?? "Unknown"),
      model: String(entry.model ?? "Model"),
      year: Number(entry.year ?? new Date().getFullYear()),
      purchaseDate: null,
      rideProfile: Array.isArray(entry.rideProfile) ? entry.rideProfile.filter(value => typeof value === "string") as BikeEquipment["rideProfile"] : ["road"],
      drivetrain: {
        groupset: String((entry.drivetrain as Record<string, unknown> | undefined)?.groupset ?? ""),
        cassette: String((entry.drivetrain as Record<string, unknown> | undefined)?.cassette ?? ""),
        chainrings: String((entry.drivetrain as Record<string, unknown> | undefined)?.chainrings ?? ""),
        chain: String((entry.drivetrain as Record<string, unknown> | undefined)?.chain ?? ""),
        bottomBracket: String((entry.drivetrain as Record<string, unknown> | undefined)?.bottomBracket ?? ""),
        crankLengthMm: null
      },
      wheels: {
        wheelset: String((entry.wheels as Record<string, unknown> | undefined)?.wheelset ?? ""),
        tireModel: String((entry.wheels as Record<string, unknown> | undefined)?.tireModel ?? ""),
        tireWidthMm: Number((entry.wheels as Record<string, unknown> | undefined)?.tireWidthMm ?? 0),
        tubeless: Boolean((entry.wheels as Record<string, unknown> | undefined)?.tubeless),
        tirePressureFrontPsi: null,
        tirePressureRearPsi: null
      },
      maintenance: {
        chainWaxDate: String((entry.maintenance as Record<string, unknown> | undefined)?.chainWaxDate ?? "") || null,
        chainMileageMiles: Number((entry.maintenance as Record<string, unknown> | undefined)?.chainMileageMiles ?? 0),
        totalMileageMiles: 0,
        brakePads: ((entry.maintenance as Record<string, unknown> | undefined)?.brakePads === "replace" || (entry.maintenance as Record<string, unknown> | undefined)?.brakePads === "watch") ? (entry.maintenance as Record<string, unknown>).brakePads as "watch" | "replace" : "good",
        rotors: "good",
        di2BatteryPercent: Number((entry.maintenance as Record<string, unknown> | undefined)?.di2BatteryPercent ?? 0),
        tireSealantDate: String((entry.maintenance as Record<string, unknown> | undefined)?.tireSealantDate ?? "") || null,
        lastServiceDate: null,
        nextServiceNote: ""
      }
    };
  });

  const migratedRecovery: RecoveryEquipment[] = recoveryValue.map((item, index) => {
    const entry = item as Record<string, unknown>;
    const createdAtIso = nowIso();
    const available = Boolean(entry.available);
    const type = entry.type === "sauna" || entry.type === "hot-tub" || entry.type === "cold-plunge" || entry.type === "massage-gun" ? entry.type : "cold-plunge";

    return {
      id: String(entry.id ?? `recovery-migrated-${index}`),
      category: "recovery",
      lifecycleStatus: available ? "active" : "planned",
      readinessStatus: available ? "ready" : "planned",
      archived: false,
      notes: "",
      createdAtIso,
      updatedAtIso: createdAtIso,
      name: String(entry.name ?? "Recovery"),
      type,
      preferredTiming: (entry.preferredTime === "morning" || entry.preferredTime === "afternoon" || entry.preferredTime === "evening") ? entry.preferredTime : null,
      defaultTemperatureF: entry.temperatureF === null ? null : Number(entry.temperatureF ?? null),
      defaultDurationMinutes: entry.durationMinutes === null ? null : Number(entry.durationMinutes ?? null),
      contrastTherapyProtocols: []
    };
  });

  const migratedSensors: SensorEquipment[] = sensorsValue.map((item, index) => {
    const entry = item as Record<string, unknown>;
    const createdAtIso = nowIso();
    const connected = entry.connected !== false;

    return {
      id: String(entry.id ?? `sensor-migrated-${index}`),
      category: "sensor",
      lifecycleStatus: connected ? "active" : "inactive",
      readinessStatus: connected ? "ready" : "unavailable",
      archived: false,
      notes: "",
      createdAtIso,
      updatedAtIso: createdAtIso,
      name: String(entry.name ?? "Sensor"),
      type: (entry.type === "bike-computer" || entry.type === "watch" || entry.type === "core-temp" || entry.type === "respiratory") ? entry.type : "watch",
      batteryPercent: entry.batteryPercent === null ? null : Number(entry.batteryPercent ?? null),
      firmware: String(entry.firmware ?? ""),
      lastSyncIso: String(entry.lastSyncIso ?? "") || null,
      assignedBikeId: null
    };
  });

  return {
    schemaVersion: EQUIPMENT_REGISTRY_SCHEMA_VERSION,
    bikes: migratedBikes,
    recoveryEquipment: migratedRecovery,
    sensors: migratedSensors,
    gymEquipment: []
  };
}

function parseRegistry(rawValue: string | null): EquipmentRegistry {
  if (!rawValue) {
    return createSeedEquipmentRegistry();
  }

  try {
    const parsed = JSON.parse(rawValue) as EquipmentRegistrySnapshot | EquipmentRegistry;
    const registry = "registry" in (parsed as Record<string, unknown>) ? (parsed as EquipmentRegistrySnapshot).registry : parsed as EquipmentRegistry;

    if (!registry || typeof registry !== "object") {
      return createSeedEquipmentRegistry();
    }

    if (registry.schemaVersion === EQUIPMENT_REGISTRY_SCHEMA_VERSION) {
      return cloneRegistry(registry);
    }

    return migrateV1ToV2(registry);
  } catch {
    return createSeedEquipmentRegistry();
  }
}

function withSavedAt(registry: EquipmentRegistry): EquipmentRegistrySnapshot {
  return {
    savedAtIso: nowIso(),
    registry
  };
}

function updateCollection<T extends EquipmentItem>(
  collection: T[],
  id: string,
  updater: (item: T) => T
): T[] {
  return collection.map(item => (item.id === id ? updater(item) : item));
}

function getCollectionForCategory(registry: EquipmentRegistry, category: EquipmentCategory): EquipmentItem[] {
  if (category === "bike") {
    return registry.bikes;
  }
  if (category === "recovery") {
    return registry.recoveryEquipment;
  }
  if (category === "sensor") {
    return registry.sensors;
  }
  return registry.gymEquipment;
}

function setCollectionForCategory(
  registry: EquipmentRegistry,
  category: EquipmentCategory,
  items: EquipmentItem[]
): EquipmentRegistry {
  if (category === "bike") {
    return { ...registry, bikes: items as BikeEquipment[] };
  }
  if (category === "recovery") {
    return { ...registry, recoveryEquipment: items as RecoveryEquipment[] };
  }
  if (category === "sensor") {
    return { ...registry, sensors: items as SensorEquipment[] };
  }
  return { ...registry, gymEquipment: items as GymEquipment[] };
}

export function createEquipmentRegistryRepository(storageAdapter: StorageAdapter = createLocalStorageAdapter()): EquipmentRegistryRepository {
  function loadRegistry(): EquipmentRegistry {
    const current = parseRegistry(storageAdapter.getItem(EQUIPMENT_REGISTRY_STORAGE_KEY));

    if (!storageAdapter.getItem(EQUIPMENT_REGISTRY_STORAGE_KEY)) {
      saveRegistry(current);
    }

    return current;
  }

  function saveRegistry(registry: EquipmentRegistry): void {
    const normalized: EquipmentRegistry = {
      ...cloneRegistry(registry),
      schemaVersion: EQUIPMENT_REGISTRY_SCHEMA_VERSION
    };
    storageAdapter.setItem(EQUIPMENT_REGISTRY_STORAGE_KEY, JSON.stringify(withSavedAt(normalized)));
  }

  function addEquipment(item: EquipmentItem): EquipmentRegistry {
    const registry = loadRegistry();
    const withTimestamp = updateItemTimestamp(item);
    const current = getCollectionForCategory(registry, item.category);
    const next = setCollectionForCategory(registry, item.category, [...current, withTimestamp]);
    saveRegistry(next);
    return next;
  }

  function updateEquipment(item: EquipmentItem): EquipmentRegistry {
    const registry = loadRegistry();
    const current = getCollectionForCategory(registry, item.category);
    const next = setCollectionForCategory(
      registry,
      item.category,
      updateCollection(current, item.id, () => updateItemTimestamp(item))
    );
    saveRegistry(next);
    return next;
  }

  function archiveEquipment(category: EquipmentCategory, id: string): EquipmentRegistry {
    const registry = loadRegistry();
    const current = getCollectionForCategory(registry, category);
    const next = setCollectionForCategory(
      registry,
      category,
      updateCollection(current, id, item => updateItemTimestamp({ ...item, archived: true, lifecycleStatus: "inactive", readinessStatus: "unavailable" }))
    );
    saveRegistry(next);
    return next;
  }

  function restoreEquipment(category: EquipmentCategory, id: string): EquipmentRegistry {
    const registry = loadRegistry();
    const current = getCollectionForCategory(registry, category);
    const next = setCollectionForCategory(
      registry,
      category,
      updateCollection(current, id, item => {
        const lifecycleStatus: EquipmentLifecycleStatus = item.lifecycleStatus === "sold" || item.lifecycleStatus === "retired" ? item.lifecycleStatus : "active";
        const readinessStatus: EquipmentReadinessStatus = "ready";
        return updateItemTimestamp({ ...item, archived: false, lifecycleStatus, readinessStatus });
      })
    );
    saveRegistry(next);
    return next;
  }

  function getDeletionWarnings(registry: EquipmentRegistry, category: EquipmentCategory, id: string): string[] {
    const warnings: string[] = [];

    if (category === "bike") {
      const bike = registry.bikes.find(entry => entry.id === id);
      const linkedSensors = registry.sensors.filter(sensor => sensor.assignedBikeId === id);
      if (linkedSensors.length > 0) {
        warnings.push(`Assigned sensors: ${linkedSensors.map(sensor => sensor.name).join(", ")}.`);
      }
      warnings.push("Deleting this bike removes maintenance history from the registry.");
      if (bike && !bike.archived && bike.lifecycleStatus === "active") {
        warnings.push("This bike may be selected by daily recommendations.");
      }
    }

    if (category === "recovery") {
      const item = registry.recoveryEquipment.find(entry => entry.id === id);
      const hasContrast = (item?.contrastTherapyProtocols.length ?? 0) > 0;
      if (hasContrast) {
        warnings.push("This recovery item has contrast-therapy protocol references.");
      }
      if (item && !item.archived && item.lifecycleStatus === "active") {
        warnings.push("This recovery item may be selected by daily recommendations.");
      }
    }

    return warnings;
  }

  function deleteEquipment(category: EquipmentCategory, id: string, options: DeleteOptions): DeleteResult {
    const registry = loadRegistry();
    const warnings = getDeletionWarnings(registry, category, id);

    if (!options.confirmed) {
      return {
        deleted: false,
        requiresConfirmation: true,
        warnings,
        registry
      };
    }

    const current = getCollectionForCategory(registry, category);
    const next = setCollectionForCategory(registry, category, current.filter(item => item.id !== id));
    saveRegistry(next);

    return {
      deleted: true,
      requiresConfirmation: false,
      warnings,
      registry: next
    };
  }

  return {
    loadRegistry,
    saveRegistry,
    addEquipment,
    updateEquipment,
    archiveEquipment,
    restoreEquipment,
    deleteEquipment,
    getDeletionWarnings
  };
}

export function createEquipmentId(category: EquipmentCategory): string {
  return `${category}-${Math.random().toString(36).slice(2, 10)}`;
}
